"use client";
import { useState, useEffect, useRef } from "react";
import {
  Slider,
  Stack,
  IconButton,
  Typography,
  ListItemText,
  Box
} from "@mui/material";
import {
  HourglassFullTwoTone as DownloadingIcon,
  SkipPrevious as PreviousIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  SkipNext as ForwardIcon,
  SentimentVeryDissatisfied as ErrorIcon
} from "@mui/icons-material";

import { useStore } from "../../../../../service/mobx";
import Tooltip from "../../../../../component/Tooltip";
import { fetchFromApiServerRaw } from "../../../../../service/graph";
import { TTSIcon } from "../../../../../component/Icon/TTS";

export default function TTS({ publisher, paperID, paperJSON }) {
  const [sections] = useState(
    paperJSON.sections.map(section => section.heading)
  );
  const [maxIndex] = useState(() => {
    const referencesIndex = sections.findIndex(section =>
      section.toLowerCase().includes("reference")
    );

    return (referencesIndex === -1 ? sections.length : referencesIndex) - 1;
  });
  const [sectionIndex, setSelectedIndex] = useState(0);
  const [tracks, setTracks] = useState([]);
  const [state, setState] = useState({
    seconds: 0,
    duration: 0,
    trackIndex: 0,
    trackDuration: []
  });
  const [action, setAction] = useState("idle");
  const { analytics, utilities } = useStore();
  const audioRef = useRef();

  useEffect(() => {
    const audio = audioRef.current;

    if (audio) {
      // Function to update the slider based on the current time
      const updateSlider = () => {
        setState(state => {
          let previousTrackTime = 0;
          let i = state.trackIndex;

          while (i--) {
            previousTrackTime += state.trackDuration[i][0];
          }

          // do not update slider state if it's paused, e.g. pausing when idle will cause react state to update before the event listener, bc of event loop order
          if (audioRef.current?.paused) {
            return state;
          }

          return { ...state, seconds: previousTrackTime + audio.currentTime };
        });

        // if (audio.currentTime === audio.duration) {
        //   setState("idle");
        // }
      };
      audio.addEventListener("timeupdate", updateSlider);
      // Cleanup event listeners
      return () => audio.removeEventListener("timeupdate", updateSlider);
    }
  }, []);

  useEffect(() => {
    if (audioRef.current?.paused === false) {
      audioRef.current.pause();
    }

    setTracks([]);
    setState({
      seconds: 0,
      duration: 0,
      trackIndex: 0,
      trackDuration: []
    });
  }, [sectionIndex]);

  useEffect(
    () => () => tracks.forEach(url => URL.revokeObjectURL(url)),
    [tracks]
  );

  function onClick(_, newAction, currentSectionIndex = sectionIndex) {
    const startingIndex = currentSectionIndex;
    newAction ??=
      action === "idle" ? "Download" : action === "Play" ? "Pause" : "Play";

    setAction(newAction);

    if (newAction !== "idle") {
      analytics.track.event(`TTS ${newAction}`, {
        index: currentSectionIndex,
        section: sections[currentSectionIndex]
      });
    }

    if (newAction === "Pause") {
      audioRef.current.pause();
    } else if (newAction === "Play") {
      audioRef.current.play()?.catch(() => {});
    } else {
      new Promise(async resolve => {
        try {
          var path = `papers/tts/${publisher}/${paperID}/${currentSectionIndex}`;
          var base64AudioFiles = await utilities.cache.get(path);

          if (!base64AudioFiles) {
            throw "no cache";
          }
        } catch {
          const response = await fetchFromApiServerRaw({
            path
          }).catch(() => {});

          if (response?.ok) {
            base64AudioFiles = await response.json();
            utilities.cache.set(path, base64AudioFiles);
          }
        }

        // only update track state and start playing the track
        // if the user is still on the same sectionIndex that they were on when
        // they initiated the action to request the audio
        if (startingIndex !== currentSectionIndex) {
          return;
        } else if (!base64AudioFiles) {
          // Set error state
          return setAction("error");
        }

        const tracks = [];

        for (let i = 0, j = base64AudioFiles.length; i < j; i++) {
          const byteCharacters = atob(base64AudioFiles[i]);
          const bytes = [];

          for (let k = 0, l = byteCharacters.length; k < l; k++) {
            bytes[k] = byteCharacters.charCodeAt(k);
          }
          // Now you can create the Blob from the Uint8Array
          const url = URL.createObjectURL(
            new Blob([new Uint8Array(bytes)], { type: "audio/mp3" })
          );
          const track = new Audio(url);

          tracks.push(url);
          track.addEventListener("loadedmetadata", () =>
            setState(state => {
              const { trackDuration } = state;
              // track duration, where to start track, how long it goes
              trackDuration[i] = [track.duration, 0, 0];

              for (
                let i = 0, j = trackDuration.length, whereToStart = 0;
                i < j;
                i++
              ) {
                var [duration = 0] = trackDuration[i] ?? [];

                trackDuration[i] = [
                  duration,
                  whereToStart,
                  (whereToStart += duration)
                ];
              }

              return {
                ...state,
                duration: state.duration + track.duration
              };
            })
          );
        }

        setTracks(tracks);
        setAction("Play");
        resolve();
      });
    }
  }
  // console.log({
  //   ...state,
  //   tracks,
  //   sectionIndex,
  //   src: audioRef.current?.src,
  //   ref: audioRef.current
  // });

  return (
    <Box
      display="flex"
      py={{ compact: 4, expanded: 0 }}
      ml={{ compact: -3, expanded: 0 }}
      justifyContent={{ compact: "center", expanded: "flex-end" }}
    >
      <Stack
        useFlexGap
        spacing={2}
        direction="row"
        px={{ compact: 0, expanded: 2 }}
        bgcolor="surface.container.lowest"
        borderRadius={theme => theme.shape.lg.round}
      >
        <Stack useFlexGap spacing={1} direction="row" alignItems="center">
          <ControlButton
            name="Previous"
            disabled={sectionIndex === 0}
            Icon={PreviousIcon}
            onClick={() => {
              const newIndex = Math.max(sectionIndex - 1, 0);

              setAction("idle");
              setSelectedIndex(newIndex);
              analytics.track.event("TTS Previous", {
                index: newIndex,
                section: sections[newIndex]
              });
            }}
          />
          <ControlButton
            primary
            name="Text to speech - Pause or Play"
            onClick={onClick}
            title={action === "idle" ? "Listen aloud" : undefined}
            disabled={action === "Download" || action === "error"}
            Icon={
              action === "Play"
                ? PauseIcon
                : action === "Pause"
                ? PlayIcon
                : action === "idle"
                ? TTSIcon
                : action === "Download"
                ? DownloadingIcon
                : ErrorIcon
            }
          />
          <ControlButton
            name="Next"
            disabled={sectionIndex === maxIndex}
            Icon={ForwardIcon}
            onClick={() => {
              const newIndex = Math.min(sectionIndex + 1, maxIndex);

              setAction("idle");
              setSelectedIndex(newIndex);
              analytics.track.event("TTS Next", {
                index: newIndex,
                section: sections[newIndex]
              });
            }}
          />
        </Stack>
        <Stack direction="row" minWidth={8 * 20}>
          <ListItemText
            primary={sections[sectionIndex]}
            primaryTypographyProps={{
              variant: "bodySm",
              color: "surface.on.color",
              sx: {
                maxWidth: "160px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }
            }}
            secondary={
              <Stack
                useFlexGap
                direction="row"
                alignItems="center"
                spacing={1.5}
                pb={1}
              >
                <TimeLabel seconds={state.seconds} />
                <Slider
                  aria-label="Time slider"
                  value={state.seconds}
                  max={state.duration || 1}
                  disabled={action === "idle"}
                  onChange={(_, seconds) => {
                    if (audioRef.current.paused === false) {
                      onClick(undefined, "Pause");
                    }

                    setState(state => ({ ...state, seconds }));
                  }}
                  onChangeCommitted={(_, seconds) => {
                    setState(state => {
                      let { trackDuration, trackIndex } = state;

                      for (let i = 0, j = trackDuration.length; i < j; i++) {
                        const runTime = trackDuration[i][2];

                        if (seconds <= runTime) {
                          trackIndex = i;
                          break;
                        }
                      }

                      setTimeout(() => {
                        if (audioRef.current.paused) {
                          audioRef.current.currentTime =
                            seconds - (trackDuration[trackIndex]?.[1] ?? 0);

                          onClick(undefined, "Play");
                        }
                      }, 100);

                      return {
                        ...state,
                        seconds,
                        trackIndex
                      };
                    });

                    analytics.track.event("TTS Skip-to", {
                      index: sectionIndex,
                      section: sections[sectionIndex],
                      skipTo: seconds / state.duration
                    });
                  }}
                  sx={{ color: "secondary.color" }}
                />
                <TimeLabel seconds={state.duration} />
              </Stack>
            }
            secondaryTypographyProps={{ component: "div" }}
          />
        </Stack>
        <audio
          autoPlay
          ref={audioRef}
          src={tracks[state.trackIndex]}
          onEnded={() => {
            const lastTrack = state.trackDuration.length - 1;
            const nextTrack = Math.min(state.trackIndex + 1, lastTrack);

            if (nextTrack !== lastTrack) {
              // play next track
              setState(state => ({ ...state, trackIndex: nextTrack }));
              setTimeout(() => audioRef.current.play()?.catch(() => {}), 100);
            } else if (sectionIndex !== maxIndex) {
              const newSection = sectionIndex + 1;
              // if finished last track, move to next section if possible
              setSelectedIndex(newSection);
              onClick(undefined, "Download", newSection);
            } else {
              // else if last section and last track, go idle
              setAction("idle");
            }
          }}
        />
      </Stack>
    </Box>
  );
}

function TimeLabel({ seconds = 0 }) {
  // Round the seconds first
  const totalSeconds = Math.round(seconds);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  let parts = [];
  // Add hours if present
  if (hours > 0) {
    parts.push(String(hours).padStart(2, "0"));
  }
  // Always add minutes, pad if hours are present
  parts.push(String(minutes).padStart(hours > 0 ? 2 : 1, "0"));
  // Always add seconds, pad to 2 digits
  parts.push(String(secs).padStart(2, "0"));

  return seconds === 0 ? (
    ""
  ) : (
    <Typography variant="labelSm" color="surface.on.color">
      {parts.join(":")}
    </Typography>
  );
}
const ControlButton = ({ name, primary, disabled, Icon, title, onClick }) => (
  <Tooltip arrow title={title}>
    <span>
      <IconButton
        aria-label={name}
        disabled={disabled}
        onClickCapture={onClick}
        sx={
          primary
            ? { bgcolor: "secondary.container" }
            : { "&:hover": { bgcolor: "surface.container.high" } }
        }
      >
        <Icon
          sx={{
            height: primary ? 20 : 16,
            width: primary ? 20 : 16,
            color: disabled
              ? "surface.container.highest"
              : primary
              ? "secondary.on.container"
              : "surface.on.variant"
          }}
        />
      </IconButton>
    </span>
  </Tooltip>
);
